import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, Checkbox, DatePicker, Empty, Input, Modal,
  Dropdown, Segmented, Select, Skeleton, Space, Table, Tabs, Tag, Tooltip,
  Typography, message,
} from 'antd';
import {
  CopyOutlined, DeleteOutlined, EditOutlined, ReloadOutlined, SearchOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import EmployeeCompletionHistory from '../components/EmployeeCompletionHistory';
import QuestionInlineEditor, { EditableQuestion } from '../components/QuestionInlineEditor';
import api, { getAssignmentProgress } from '../services/api';
import { AssignmentProgressAttempt, AssignmentProgressResponse, AssignmentProgressRow, Test, TestDifficulty, User } from '../types';
import { TEST_SUBGROUP_OPTIONS } from '../constants/testSubgroups';
import {
  TEST_DIFFICULTY_COLOR,
  TEST_DIFFICULTY_LABEL,
  TEST_DIFFICULTY_OPTIONS,
} from '../constants/testDifficulty';
import { getApiErrorMessage } from '../utils/apiError';
import './AdminAssignmentBoard.css';

const { Title, Text, Paragraph } = Typography;

/**
 * Рабочий стол назначений.
 *
 * Собран вокруг одного состояния назначения, которое отдаёт backend
 * (effective_state), вместо пары is_active + status: они менялись независимо
 * и расходились, из-за чего «выдано» и «видно сотруднику» переставали
 * совпадать. Подготовленные назначения намеренно отделены от
 * приостановленных: первые создают заранее, вторые — закрытый доступ.
 */

type AssignmentState = 'available' | 'prepared' | 'paused' | 'completed' | 'expired';
type Cut = 'tests' | 'users' | 'retake';

interface Assignment {
  id: number;
  user_id: number;
  test_id: number;
  due_at: string | null;
  assigned_at: string;
  is_required: boolean;
  status: string;
  effective_state: AssignmentState;
  user_name?: string;
  test_title?: string;
  test_subgroup?: string | null;
  test_questions_count?: number;
  latest_attempt_id?: number | null;
  latest_attempt_score?: number | null;
  latest_attempt_is_passed?: boolean | null;
  latest_attempt_completed_at?: string | null;
}

type TestLifecycle = 'draft' | 'published' | 'archived';

const testLifecycle = (test: Test): TestLifecycle => (
  test.is_archived ? 'archived' : (test.is_active ? 'published' : 'draft')
);

const TEST_LIFECYCLE_LABEL: Record<TestLifecycle, string> = {
  draft: 'Черновик',
  published: 'Опубликован',
  archived: 'Архив',
};

const HISTORY_EVENT_LABEL: Record<string, string> = {
  assigned: 'назначен',
  reassigned: 'перевыдан',
  activated: 'выдан',
  deactivated: 'приостановлен',
  renewed: 'продлён',
  restarted: 'перезапущен после технического сбоя',
  deleted: 'удалён',
};

const STATE_LABEL: Record<AssignmentState, string> = {
  available: 'Выдано',
  prepared: 'Подготовлено',
  paused: 'Приостановлено',
  completed: 'Завершено',
  expired: 'Просрочено',
};

const confirmSimilarTest = (
  detail: any,
  onConfirm: () => Promise<void>,
) => {
  const conflicts = Array.isArray(detail?.conflicts) ? detail.conflicts : [];
  Modal.confirm({
    title: 'Похожий тест уже назначался',
    width: 640,
    content: (
      <div>
        <p>
          У сотрудника уже есть назначенный, подготовленный или завершённый
          тест с совпадением не менее 80% вопросов. Проверьте, действительно
          ли нужен ещё один.
        </p>
        <ul>
          {conflicts.slice(0, 8).map((item: any, index: number) => (
            <li key={`${item.assignment_id}-${item.target_assignment_id || index}`}>
              {item.user_name || `Сотрудник #${item.user_id}`}: «{item.test_title}»
              {' '}({Math.round(Number(item.overlap_percent) || 0)}% совпадения)
            </li>
          ))}
        </ul>
        {conflicts.length > 8 && <p>И ещё конфликтов: {conflicts.length - 8}.</p>}
      </div>
    ),
    okText: 'Всё равно выдать',
    okType: 'danger',
    cancelText: 'Отмена',
    onOk: onConfirm,
  });
};

const STATE_ORDER: AssignmentState[] = ['available', 'prepared', 'paused', 'completed', 'expired'];

const StateTag: React.FC<{ state: AssignmentState; count?: number }> = ({ state, count }) => (
  <span className={`ab-state ab-state--${state}`}>
    {STATE_LABEL[state]}{count !== undefined ? ` ${count}` : ''}
  </span>
);

const normalizeSearch = (value: string) => value.trim().replace(/\s+/g, ' ').toLocaleLowerCase('ru-RU');
const matchesSearch = (text: string, needle: string) => normalizeSearch(text).includes(needle);
const testQuestionTexts = (test: Test) => Array.isArray(test.question_texts) ? test.question_texts : [];

const highlightQuestionMatch = (text: string, needle: string): React.ReactNode => {
  if (!needle) return text;
  // Treat the input as literal text; spaces also match line breaks in a question.
  const pattern = needle.split(' ').map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('\\s+');
  return text.split(new RegExp(`(${pattern})`, 'giu')).map((part, index) => (
    index % 2 ? <mark className="ab-question-hit" key={index}>{part}</mark> : part
  ));
};

const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];

const monthKey = (iso?: string) => (iso || '').slice(0, 7);
const monthLabel = (key: string) => {
  const [year, month] = key.split('-');
  return `${MONTHS[Number(month) - 1] || month} ${year}`;
};

// Progress is read-only: classifications come from the Sprint 5 response.
const progressRowKey = (row: AssignmentProgressRow) =>
  `${row.assignment_id}:${row.user_id}:${row.test_id}`;

const progressDate = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return null;
  // Persisted naive timestamps are UTC, as in the progress contract.
  const iso = /(?:Z|[+-][0-9]{2}:[0-9]{2})$/i.test(value) ? value : `${value}Z`;
  const date = dayjs(iso);
  return date.isValid() ? date : null;
};

const progressAttempts = (row: AssignmentProgressRow): AssignmentProgressAttempt[] => (
  Array.isArray(row.attempts) ? row.attempts.filter((attempt) => (
    attempt && typeof attempt === 'object'
    && attempt.assignment_id === row.assignment_id
    && attempt.user_id === row.user_id && attempt.test_id === row.test_id
  )).slice().sort((a, b) => {
    const first = progressDate(a.started_at)?.valueOf();
    const second = progressDate(b.started_at)?.valueOf();
    if (first !== second) {
      if (first === undefined) return 1;
      if (second === undefined) return -1;
      return first - second;
    }
    // dayjs retains milliseconds; preserve the backend's remaining UTC microseconds.
    if (first !== undefined) {
      const remainder = (value: string | null) => Number(
        (typeof value === 'string'
          ? value.match(/\.(\d{1,6})(?:Z|[+-][0-9]{2}:[0-9]{2})?$/i)?.[1] || '' : '')
          .padEnd(6, '0').slice(3)
      );
      const difference = remainder(a.started_at) - remainder(b.started_at);
      if (difference) return difference;
    }
    return (Number.isSafeInteger(a.attempt_id) ? a.attempt_id : 0)
      - (Number.isSafeInteger(b.attempt_id) ? b.attempt_id : 0);
  }) : []
);

const renderProgressAttempt = (attempt?: AssignmentProgressAttempt) => {
  if (!attempt) return <Tag>Нет попытки</Tag>;
  let label = 'Состояние неизвестно';
  let color: string | undefined;
  if (attempt.completed === true) {
    label = 'Завершена · результат неизвестен';
    if (attempt.malformed === false && attempt.outcome === 'passed'
      && attempt.state === 'completed_passed') {
      label = 'Завершена · сдано'; color = 'green';
    } else if (attempt.malformed === false && attempt.outcome === 'failed'
      && attempt.state === 'completed_failed') {
      label = 'Завершена · не сдано'; color = 'red';
    }
  } else {
    switch (attempt.state) {
      case 'in_progress': label = 'В процессе'; break;
      case 'in_progress_unknown': label = 'Не завершена · активность неизвестна'; break;
      case 'abandoned': label = 'Не завершена · прекращена'; break;
      default: break;
    }
  }
  const date = progressDate(attempt.started_at)?.format('DD.MM.YYYY HH:mm') || 'Без даты';
  const score = typeof attempt.score === 'number' && Number.isFinite(attempt.score)
    && attempt.score >= 0 && attempt.score <= 100 ? `${attempt.score}%` : '—';
  return <Tag className="ab-attempt-tag" color={color} style={{ whiteSpace: 'normal', marginInlineEnd: 0 }}>{date} · {score} · {label}</Tag>;
};

const renderProgressScore = (row: AssignmentProgressRow) => {
  const completed = progressAttempts(row).filter((attempt) => attempt.completed === true);
  const latest = completed[completed.length - 1];
  const score = latest?.score;
  return <Text className="ab-score">{latest?.malformed === false
    && typeof score === 'number' && Number.isFinite(score) && score >= 0 && score <= 100
    ? `${Math.round(score * 10) / 10}%` : '—'}</Text>;
};

const renderProgressFinal = (row: AssignmentProgressRow) => {
  if (row.flags?.passed === true || row.status === 'passed') {
    return <Tag color="green">{row.flags?.passed_after_retake === true
      ? 'Сдано после пересдачи' : 'Сдано'}</Tag>;
  }
  switch (row.status) {
    case 'needs_retake': return <Tag color="orange">Нужна пересдача</Tag>;
    case 'not_started': return <Tag>Не начат</Tag>;
    case 'in_progress': return <Tag>{row.activity === 'in_progress_unknown'
      ? 'Активность неизвестна' : 'В процессе'}</Tag>;
    case 'abandoned': return <Tag>Не завершён · прекращён</Tag>;
    default: return <Tag>Результат неизвестен</Tag>;
  }
};

// Progress of one test requested for the selected employee. The row source stays the assignment list.
interface UserProgressEntry {
  status: 'loading' | 'ready' | 'error';
  data?: AssignmentProgressResponse;
  error?: string;
}

const PROGRESS_SUMMARY: { key: keyof AssignmentProgressResponse['summary']; label: string; color?: string }[] = [
  { key: 'assigned_total', label: 'Всего назначено' },
  { key: 'first_try_passed', label: 'Сдали с первой попытки', color: 'green' },
  { key: 'first_try_failed', label: 'Не сдали с первой попытки', color: 'red' },
  { key: 'passed_after_retake', label: 'Сдали после пересдачи', color: 'green' },
  { key: 'needs_retake', label: 'Нужна пересдача', color: 'orange' },
  { key: 'not_started', label: 'Не начали' },
];

// Page-wide bound for /admin/tests/{id}/assignment-progress, shared by «По тестам», «Пересдачи» and «По сотрудникам».
const PROGRESS_REQUEST_LIMIT = 4;

interface RequestWaiter {
  isCurrent: () => boolean;
  resolve: (granted: boolean) => void;
}

interface RequestLimiter {
  run: <T>(task: () => Promise<T>, isCurrent: () => boolean) => Promise<T | null>;
}

// FIFO semaphore. Only the factory is module-level; every page instance owns its limiter (useRef below).
// A slot is held from the transport call until that promise settles, even if the caller's generation is gone.
// run() resolves null without calling task when the generation ended before a slot was granted.
const createRequestLimiter = (limit: number): RequestLimiter => {
  let active = 0;
  const waiting: RequestWaiter[] = [];
  const acquireSlot = (isCurrent: () => boolean) => {
    if (!isCurrent()) return Promise.resolve(false);
    // Invariant: waiting is non-empty only while active === limit, so nobody overtakes the queue.
    if (active < limit) {
      active += 1;
      return Promise.resolve(true);
    }
    return new Promise<boolean>((resolve) => { waiting.push({ isCurrent, resolve }); });
  };
  const releaseSlot = () => {
    while (waiting.length > 0) {
      const next = waiting.shift() as RequestWaiter;
      if (next.isCurrent()) {
        next.resolve(true); // the slot passes straight to the oldest live waiter; active is unchanged
        return;
      }
      next.resolve(false); // stale waiter: dropped without ever taking a slot
    }
    active -= 1;
  };
  return {
    run: async (task, isCurrent) => {
      if (!(await acquireSlot(isCurrent))) return null;
      try {
        if (!isCurrent()) return null;
        return await task();
      } finally {
        releaseSlot();
      }
    },
  };
};

const AdminAssignmentBoard: React.FC = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState<Test[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [subgroups, setSubgroups] = useState<string[]>([...TEST_SUBGROUP_OPTIONS]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [cut, setCut] = useState<Cut>('tests');
  const [query, setQuery] = useState('');
  const [subgroup, setSubgroup] = useState<string | undefined>();
  const [difficulty, setDifficulty] = useState<TestDifficulty | 'unset' | undefined>();
  const [month, setMonth] = useState<string | undefined>();
  const [lifecycle, setLifecycle] = useState<TestLifecycle | undefined>();
  const [states, setStates] = useState<AssignmentState[]>([]);
  const [selectedTestId, setSelectedTestId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const [detailTab, setDetailTab] = useState('people');
  const [questions, setQuestions] = useState<EditableQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsTestId, setQuestionsTestId] = useState<number | null>(null);
  const [activeQuestionMatchId, setActiveQuestionMatchId] = useState<number | null>(null);
  const questionLoadGeneration = useRef(0);
  const questionTableRef = useRef<HTMLDivElement>(null);
  const [progressState, setProgressState] = useState<{ testId: number; status: 'loading' | 'error' | 'ready'; data?: AssignmentProgressResponse; error?: string } | null>(null);
  const retakePending = useRef(new Set<number>());
  const progressLimiter = useRef<RequestLimiter | null>(null);
  const [progressRevision, setProgressRevision] = useState(0);
  const [retakeProgress, setRetakeProgress] = useState<Record<number, AssignmentProgressResponse>>({});
  const [retakeProgressErrors, setRetakeProgressErrors] = useState<Record<number, string>>({});
  const [retakeProgressLoading, setRetakeProgressLoading] = useState(false);
  const [userProgress, setUserProgress] = useState<{ userId: number; tests: Record<number, UserProgressEntry> } | null>(null);
  const [attemptHistoryKey, setAttemptHistoryKey] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditableQuestion | null>(null);

  const [editTest, setEditTest] = useState<Test | null>(null);
  const [editDraft, setEditDraft] = useState({
    title: '', description: '', subgroup: '', difficulty: '' as TestDifficulty | '', time_limit_minutes: 30,
  });

  const [mergeIds, setMergeIds] = useState<number[]>([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTitle, setMergeTitle] = useState('');
  const [mergeLimit, setMergeLimit] = useState(30);

  const [reassign, setReassign] = useState<Assignment | null>(null);
  const [reassignUserId, setReassignUserId] = useState<number | null>(null);

  const [imagesOpen, setImagesOpen] = useState(false);
  const [imagesPath, setImagesPath] = useState('');

  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignUserIds, setAssignUserIds] = useState<number[]>([]);
  const [deadline, setDeadline] = useState<Dayjs | null>(null);
  const [releaseNow, setReleaseNow] = useState(true);
  const [renewing, setRenewing] = useState<Assignment | null>(null);
  const [renewDeadline, setRenewDeadline] = useState<Dayjs | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [testsResponse, usersResponse, assignmentsResponse, subgroupResponse] = await Promise.all([
        api.get<Test[]>('/admin/tests'),
        api.get<User[]>('/admin/users'),
        api.get<Assignment[]>('/admin/test-assignments'),
        api.get<string[]>('/admin/test-subgroups'),
      ]);
      setTests(testsResponse.data || []);
      setUsers(usersResponse.data || []);
      setAssignments(assignmentsResponse.data || []);
      setSubgroups(subgroupResponse.data || [...TEST_SUBGROUP_OPTIONS]);
    } catch (error) {
      message.error(getApiErrorMessage(error, 'не удалось загрузить данные'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadQuestions = useCallback(async (testId: number) => {
    const generation = ++questionLoadGeneration.current;
    setQuestionsLoading(true);
    setQuestionsTestId(testId);
    setQuestions([]);
    try {
      const { data } = await api.get<EditableQuestion[]>(`/admin/tests/${testId}/questions`);
      if (generation !== questionLoadGeneration.current) return;
      const items = Array.isArray(data) ? data : [];
      setQuestions(items);
      // Refresh the catalog search after editing: removed wording must stop matching.
      setTests((current) => current.map((test) => test.id === testId
        ? { ...test, question_texts: items.map((item) => item.text), questions_count: items.length }
        : test));
    } catch (error) {
      if (generation === questionLoadGeneration.current) {
        message.error(getApiErrorMessage(error, 'не удалось загрузить вопросы'));
      }
    } finally {
      if (generation === questionLoadGeneration.current) setQuestionsLoading(false);
    }
  }, []);

  const refreshAfterQuestionEdit = useCallback(async (testId: number) => {
    await loadQuestions(testId);
    try {
      // A scoped edit can change copies in other tests, so refresh their search text too.
      const { data } = await api.get<Test[]>('/admin/tests');
      setTests(Array.isArray(data) ? data : []);
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Вопрос сохранён, но не удалось обновить поиск по всем тестам'));
    }
  }, [loadQuestions]);

  useEffect(() => {
    if (!selectedTestId || detailTab !== 'questions') return undefined;
    loadQuestions(selectedTestId);
    return () => { questionLoadGeneration.current += 1; };
  }, [selectedTestId, detailTab, loadQuestions]);

  // Переход из банка вопросов: /admin/board?test=317&tab=questions
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const testParam = searchParams.get('test');
    if (testParam) {
      setSelectedTestId(Number(testParam));
      setCut('tests');
      if (searchParams.get('tab') === 'questions') setDetailTab('questions');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const byTest = useMemo(() => {
    const map = new Map<number, Assignment[]>();
    assignments.forEach((item) => {
      const list = map.get(item.test_id) || [];
      list.push(item);
      map.set(item.test_id, list);
    });
    return map;
  }, [assignments]);

  const byUser = useMemo(() => {
    const map = new Map<number, Assignment[]>();
    assignments.forEach((item) => {
      const list = map.get(item.user_id) || [];
      list.push(item);
      map.set(item.user_id, list);
    });
    return map;
  }, [assignments]);

  const stateTotals = useMemo(() => {
    const totals = {} as Record<AssignmentState, number>;
    assignments.forEach((item) => {
      totals[item.effective_state] = (totals[item.effective_state] || 0) + 1;
    });
    return totals;
  }, [assignments]);

  const months = useMemo(
    () => Array.from(new Set(tests.map((t) => monthKey(t.created_at)).filter(Boolean)))
      .sort().reverse(),
    [tests],
  );

  const visibleTests = useMemo(() => {
    const needle = normalizeSearch(query);
    return tests.filter((test) => {
      const own = byTest.get(test.id) || [];
      if (states.length && !own.some((a) => states.includes(a.effective_state))) return false;
      if (subgroup && test.subgroup !== subgroup) return false;
      if (difficulty === 'unset' && test.difficulty) return false;
      if (difficulty && difficulty !== 'unset' && test.difficulty !== difficulty) return false;
      if (month && monthKey(test.created_at) !== month) return false;
      if (lifecycle && testLifecycle(test) !== lifecycle) return false;
      if (needle) {
        const titleMatches = matchesSearch(`${test.title} ${test.subgroup || ''}`, needle);
        if (!titleMatches && !testQuestionTexts(test).some((text) => matchesSearch(text, needle))) return false;
      }
      return true;
    });
  }, [tests, byTest, states, subgroup, difficulty, month, lifecycle, query]);

  const questionSearch = cut === 'tests' ? normalizeSearch(query) : '';
  const displayedQuestions = questionsTestId === selectedTestId ? questions : [];
  const matchingQuestions = useMemo(() => questionSearch && questionsTestId === selectedTestId
    ? questions.filter((question) => matchesSearch(question.text, questionSearch)) : [],
  [questions, questionsTestId, selectedTestId, questionSearch]);
  const matchingQuestionKey = matchingQuestions.map((question) => question.id).join(',');
  const firstQuestionMatchId = matchingQuestions[0]?.id ?? null;
  const selectedTestHasQuestionMatch = !!questionSearch && testQuestionTexts(tests.find((test) => test.id === selectedTestId) || {} as Test)
    .some((text) => matchesSearch(text, questionSearch));
  const activeQuestionMatchIndex = Math.max(0, matchingQuestions.findIndex((question) => question.id === activeQuestionMatchId));

  useEffect(() => {
    setActiveQuestionMatchId(firstQuestionMatchId);
  }, [selectedTestId, questionSearch, matchingQuestionKey, firstQuestionMatchId]);

  useEffect(() => {
    if (!activeQuestionMatchId || detailTab !== 'questions' || questionsLoading) return;
    const row = questionTableRef.current?.querySelector(`[data-row-key="${activeQuestionMatchId}"]`);
    row?.scrollIntoView?.({ block: 'center', inline: 'nearest' });
  }, [activeQuestionMatchId, detailTab, questionsLoading, selectedTestId]);

  const selectTest = (test: Test) => {
    setSelectedTestId(test.id);
    if (questionSearch && testQuestionTexts(test).some((text) => matchesSearch(text, questionSearch))) {
      setDetailTab('questions');
    }
  };

  // A new query also reveals matches in the currently selected test.
  useEffect(() => {
    if (selectedTestHasQuestionMatch) setDetailTab('questions');
  }, [selectedTestId, questionSearch, selectedTestHasQuestionMatch]);

  const visibleUsers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return users.filter((item) => {
      if (item.role === 'admin' && !needle) return true;
      if (!needle) return true;
      return `${item.full_name} ${item.department || ''}`.toLowerCase().includes(needle);
    });
  }, [users, query]);

  // The assignment list endpoint is the complete bounded source for assigned tests.
  // Simplified assignment state can lag attempt history (for example after an abandoned retake),
  // so nominate every assigned test and let assignment-progress classify retakes authoritatively.
  const retakeCandidateTestIds = useMemo(() => Array.from(new Set(assignments
    .map((assignment) => assignment.test_id)
    .filter((testId) => Number.isSafeInteger(testId) && testId > 0))), [assignments]);

  const retakeCandidates = useMemo(() => {
    const seen = new Set<string>();
    return retakeCandidateTestIds.flatMap((testId) => {
      const response = retakeProgress[testId];
      if (!response || response.test_id !== testId || !Array.isArray(response.rows)) return [];
      return response.rows.filter((row) => {
        if (!row || typeof row !== 'object' || row.test_id !== testId
          || ![row.assignment_id, row.user_id, row.test_id]
            .every((id) => Number.isSafeInteger(id) && id > 0)) return false;
        const needsRetake = row.flags?.needs_retake === true || row.status === 'needs_retake';
        const attempts = progressAttempts(row);
        const hasPriorSuccess = row.flags?.passed === true || row.flags?.first_try_passed === true
          || row.flags?.passed_after_retake === true || row.status === 'passed'
          || attempts.some((attempt) => attempt.completed === true && attempt.malformed === false
            && attempt.outcome === 'passed' && attempt.state === 'completed_passed');
        const key = progressRowKey(row);
        if (!needsRetake || hasPriorSuccess || seen.has(key)) return false;
        seen.add(key);
        return true;
      }).map((row) => ({ row, assignment: assignments.find((item) => (
        item.id === row.assignment_id && item.user_id === row.user_id && item.test_id === row.test_id
      )) })).filter((item): item is { row: AssignmentProgressRow; assignment: Assignment } => !!item.assignment);
    });
  }, [retakeCandidateTestIds, retakeProgress, assignments]);

  const stateSummary = (list: Assignment[]) => {
    const totals = {} as Record<AssignmentState, number>;
    list.forEach((a) => { totals[a.effective_state] = (totals[a.effective_state] || 0) + 1; });
    return STATE_ORDER.filter((s) => totals[s]).map((s) => (
      <StateTag key={s} state={s} count={totals[s]} />
    ));
  };

  const release = async (assignment: Assignment) => {
    const complete = async (allowSimilarTest: boolean) => {
      await api.patch(`/admin/test-assignments/${assignment.id}`, {
        is_active: true,
        allow_similar_test: allowSimilarTest,
      });
      message.success(`Выдано: ${assignment.user_name}`);
      await load();
    };
    setBusy(true);
    try {
      await complete(false);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === 'similar_test_confirmation_required') {
        confirmSimilarTest(detail, async () => {
          setBusy(true);
          try {
            await complete(true);
          } catch (retryError) {
            message.error(getApiErrorMessage(retryError, 'не удалось выдать'));
            throw retryError;
          } finally {
            setBusy(false);
          }
        });
      } else {
        message.error(getApiErrorMessage(error, 'не удалось выдать'));
      }
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (assignment: Assignment) => {
    setBusy(true);
    try {
      await api.patch(`/admin/test-assignments/${assignment.id}`, { is_active: false });
      message.success(`Доступ закрыт: ${assignment.user_name}`);
      await load();
    } catch (error) {
      message.error(getApiErrorMessage(error, 'не удалось приостановить'));
    } finally {
      setBusy(false);
    }
  };

  const releaseAllPrepared = async (list: Assignment[]) => {
    const prepared = list.filter((a) => a.effective_state === 'prepared');
    if (!prepared.length) return;
    const complete = async (allowSimilarTest: boolean) => {
      await api.patch('/admin/test-assignments/bulk-activity', {
        assignment_ids: prepared.map((item) => item.id),
        is_active: true,
        allow_similar_test: allowSimilarTest,
      });
      message.success(`Выдано назначений: ${prepared.length}`);
      await load();
    };
    setBusy(true);
    try {
      await complete(false);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === 'similar_test_confirmation_required') {
        confirmSimilarTest(detail, async () => {
          setBusy(true);
          try {
            await complete(true);
          } catch (retryError) {
            message.error(getApiErrorMessage(retryError, 'не удалось выдать все'));
            throw retryError;
          } finally {
            setBusy(false);
          }
        });
      } else {
        message.error(getApiErrorMessage(error, 'не удалось выдать все'));
      }
    } finally {
      setBusy(false);
    }
  };

  const retake = async (assignment: Assignment) => {
    const issue = async (allowSimilarTest = false, clearExpiredDeadline = false) => {
      if (retakePending.current.has(assignment.id)) return;
      retakePending.current.add(assignment.id);
      setBusy(true);
      try {
        await api.post('/admin/test-assignments', {
          test_id: assignment.test_id,
          user_ids: [assignment.user_id],
          expected_assignment_id: assignment.id,
          expected_assigned_at: assignment.assigned_at,
          is_active: true,
          allow_retake: true,
          allow_similar_test: allowSimilarTest,
          ...(clearExpiredDeadline ? { due_at: null } : {}),
        });
        message.success(`Пересдача выдана: ${assignment.user_name}`);
        setProgressRevision((revision) => revision + 1);
        await load();
      } catch (error: any) {
        const detail = error?.response?.data?.detail;
        if (detail?.code === 'similar_test_confirmation_required') {
          confirmSimilarTest(detail, () => issue(true, clearExpiredDeadline));
        } else if (detail?.code === 'retake_due_at_expired') {
          Modal.confirm({
            title: 'Срок назначения истёк',
            content: 'Выдать пересдачу без ограничения срока? Срок будет снят только после подтверждения.',
            okText: 'Выдать без срока',
            cancelText: 'Отмена',
            onOk: () => issue(allowSimilarTest, true),
          });
        } else {
          message.error(getApiErrorMessage(error, 'не удалось выдать пересдачу'));
        }
      } finally {
        retakePending.current.delete(assignment.id);
        setBusy(false);
      }
    };
    await issue();
  };

  const renewExpired = async () => {
    if (!renewing || !renewDeadline) return;
    const complete = async (allowSimilarTest: boolean) => {
      await api.patch(`/admin/test-assignments/${renewing.id}/renew`, {
        due_at: renewDeadline.endOf('day').toISOString(),
        allow_similar_test: allowSimilarTest,
      });
      message.success(`Назначение продлено: ${renewing.user_name}`);
      setRenewing(null);
      setRenewDeadline(null);
      await load();
    };
    setBusy(true);
    try {
      await complete(false);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === 'similar_test_confirmation_required') {
        confirmSimilarTest(detail, async () => {
          setBusy(true);
          try {
            await complete(true);
          } catch (retryError) {
            message.error(getApiErrorMessage(retryError, 'не удалось продлить назначение'));
            throw retryError;
          } finally {
            setBusy(false);
          }
        });
      } else {
        message.error(getApiErrorMessage(error, 'не удалось продлить назначение'));
      }
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async (test: Test) => {
    setBusy(true);
    try {
      const response = await api.get(`/admin/tests/${test.id}/export-template`,
        { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `test_${test.id}_template.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error(getApiErrorMessage(error, 'не удалось скачать шаблон'));
    } finally {
      setBusy(false);
    }
  };

  const patchTest = async (test: Test, body: Record<string, unknown>, note: string) => {
    setBusy(true);
    try {
      await api.patch(`/admin/tests/${test.id}`, body);
      message.success(note);
      await load();
    } catch (error) {
      message.error(getApiErrorMessage(error, 'не удалось сохранить'));
    } finally {
      setBusy(false);
    }
  };

  const duplicateTest = async (test: Test) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tests/${test.id}/duplicate`);
      message.success('Создана копия теста');
      await load();
      if (data?.id) setSelectedTestId(data.id);
    } catch (error) {
      message.error(getApiErrorMessage(error, 'не удалось дублировать'));
    } finally {
      setBusy(false);
    }
  };

  const removeTest = (test: Test) => {
    const own = byTest.get(test.id) || [];
    Modal.confirm({
      title: `Удалить «${test.title}»?`,
      content: own.length
        ? `У теста ${own.length} назначений. Вместе с тестом удалятся его вопросы, назначения и результаты прохождений. Действие необратимо.`
        : 'Вместе с тестом удалятся его вопросы. Действие необратимо.',
      okText: 'Удалить', okButtonProps: { danger: true }, cancelText: 'Отмена',
      onOk: async () => {
        await api.delete(`/admin/tests/${test.id}`);
        message.success('Тест удалён');
        setSelectedTestId(null);
        await load();
      },
    });
  };

  const openEdit = (test: Test) => {
    setEditTest(test);
    setEditDraft({
      title: test.title,
      description: test.description || '',
      subgroup: test.subgroup || '',
      difficulty: test.difficulty || '',
      time_limit_minutes: test.time_limit_minutes,
    });
  };

  const submitMerge = async () => {
    setBusy(true);
    try {
      await api.post('/admin/tests/merge-assignments', {
        assignment_ids: mergeIds,
        title: mergeTitle.trim(),
        time_limit_minutes: mergeLimit,
        is_required: true,
        assignment_is_active: true,
        allow_retake: true,
      });
      message.success('Тесты объединены');
      setMergeOpen(false);
      setMergeIds([]);
      setMergeTitle('');
      await load();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === 'merge_conflicting_duplicate_questions') {
        // Одинаковый текст и структура, но разный верный ответ — объединение
        // выбрало бы версию за администратора, поэтому отказываем явно.
        Modal.error({
          title: 'Объединение отменено',
          width: 620,
          content: (
            <div>
              <p>{detail.message}</p>
              <ul>
                {(detail.conflicts || []).slice(0, 5).map((c: any) => (
                  <li key={c.question_ids.join('-')}>{c.question_text}</li>
                ))}
              </ul>
              <p>Разберите расхождение в «Банке вопросов» и повторите.</p>
            </div>
          ),
        });
      } else {
        message.error(getApiErrorMessage(error, 'не удалось объединить'));
      }
    } finally {
      setBusy(false);
    }
  };

  const deleteAssignment = (assignment: Assignment) => {
    Modal.confirm({
      title: 'Удалить назначение?',
      content: `У сотрудника ${assignment.user_name} пропадёт доступ к тесту. `
        + 'Пройденные попытки и их результаты сохранятся.',
      okText: 'Удалить', okButtonProps: { danger: true }, cancelText: 'Отмена',
      onOk: async () => {
        await api.delete(`/admin/test-assignments/${assignment.id}`);
        message.success('Назначение удалено');
        await load();
      },
    });
  };

  const submitReassign = async () => {
    if (!reassign || !reassignUserId) return;
    setBusy(true);
    try {
      await api.post(`/admin/test-assignments/${reassign.id}/assign-user`, {
        user_id: reassignUserId,
        is_active: true,
        allow_retake: true,
      });
      message.success('Назначено другому сотруднику');
      setReassign(null);
      setReassignUserId(null);
      await load();
    } catch (error) {
      message.error(getApiErrorMessage(error, 'не удалось переназначить'));
    } finally {
      setBusy(false);
    }
  };

  const importImages = async () => {
    if (!selectedTestId) return;
    setBusy(true);
    try {
      // Параметры идут в query: эндпоинт объявляет их обычными аргументами,
      // а прежний вызов слал их в теле и получал 422 — импорт не работал.
      const { data } = await api.post('/images/import-from-categories', null, {
        params: {
          base_folder_path: imagesPath.trim().replace(/^['"]|['"]$/g, ''),
          test_id: selectedTestId,
        },
      });
      message.success(
        `Импортировано вопросов: ${data.total_questions_created ?? 0}`
        + (data.total_categories ? ` из ${data.total_categories} категорий` : ''),
      );
      setImagesOpen(false);
      setImagesPath('');
      await load();
    } catch (error) {
      message.error(getApiErrorMessage(error, 'не удалось импортировать изображения'));
    } finally {
      setBusy(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const { data } = await api.get('/admin/assignment-history/tree');
      const tree = Array.isArray(data) ? data : (data?.items || []);
      setHistory(tree.flatMap((user: any) => (user.months || []).map((item: any) => ({
        key: `${user.user_id}-${item.month}`,
        user_id: user.user_id,
        user_name: user.user_name,
        department: user.department,
        month: item.month,
        month_label: item.month_label,
        tests: item.assignments || [],
      }))));
    } catch (error) {
      message.error(getApiErrorMessage(error, 'не удалось загрузить историю'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const submitAssign = async () => {
    if (!selectedTestId || !assignUserIds.length) return;
    const payload = {
      test_id: selectedTestId,
      user_ids: assignUserIds,
      due_at: deadline ? deadline.endOf('day').toISOString() : null,
      is_required: true,
      is_active: releaseNow,
    };
    const complete = async (allowRetake: boolean, allowSimilarTest = false) => {
      await api.post('/admin/test-assignments', {
        ...payload,
        allow_retake: allowRetake,
        allow_similar_test: allowSimilarTest,
      });
      message.success(releaseNow
        ? `Выдано сотрудникам: ${assignUserIds.length}`
        : `Подготовлено назначений: ${assignUserIds.length}`);
      setAssignOpen(false);
      setAssignUserIds([]);
      await load();
    };

    setBusy(true);
    try {
      await complete(false, false);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === 'retake_confirmation_required') {
        const names = (detail.conflicts || [])
          .map((item: any) => item.user_name)
          .filter(Boolean);
        Modal.confirm({
          title: 'Подтвердите повторную выдачу',
          width: 560,
          content: (
            <div>
              <p>У этих сотрудников уже есть завершённое прохождение:</p>
              <ul>{names.map((name: string) => <li key={name}>{name}</li>)}</ul>
              <p>Повторная выдача откроет пересдачу, но сохранит прежние результаты.</p>
            </div>
          ),
          okText: 'Выдать пересдачу',
          cancelText: 'Отмена',
          onOk: async () => {
            setBusy(true);
            try {
              try {
                await complete(true, false);
              } catch (retryError: any) {
                const retryDetail = retryError?.response?.data?.detail;
                if (retryDetail?.code === 'similar_test_confirmation_required') {
                  confirmSimilarTest(retryDetail, async () => {
                    await complete(true, true);
                  });
                  return;
                }
                throw retryError;
              }
            } catch (retryError) {
              message.error(getApiErrorMessage(retryError, 'не удалось назначить'));
              throw retryError;
            } finally {
              setBusy(false);
            }
          },
        });
      } else if (detail?.code === 'similar_test_confirmation_required') {
        confirmSimilarTest(detail, async () => {
          setBusy(true);
          try {
            await complete(false, true);
          } catch (retryError) {
            message.error(getApiErrorMessage(retryError, 'не удалось назначить'));
            throw retryError;
          } finally {
            setBusy(false);
          }
        });
      } else {
        message.error(getApiErrorMessage(error, 'не удалось назначить'));
      }
    } finally {
      setBusy(false);
    }
  };

  const selectedTest = selectedTestId ? tests.find((t) => t.id === selectedTestId) : null;
  const selectedTestAssignments = selectedTestId ? (byTest.get(selectedTestId) || []) : [];

  // The only entry point to getAssignmentProgress on this page: every mode waits on the same limiter.
  // isCurrent is the caller's generation; once it turns false the answer is never applied.
  const requestProgress = useCallback((testId: number, isCurrent: () => boolean) => {
    if (!progressLimiter.current) progressLimiter.current = createRequestLimiter(PROGRESS_REQUEST_LIMIT);
    return progressLimiter.current.run(() => getAssignmentProgress(testId), isCurrent);
  }, []);

  useEffect(() => {
    if (cut !== 'tests' || !selectedTest || detailTab !== 'people') return undefined;
    // Generation of this test/tab/revision; cleanup ends it.
    let current = true;
    const testId = selectedTest.id;
    setProgressState({ testId, status: 'loading' });
    requestProgress(testId, () => current).then((response) => {
      if (current && response) setProgressState({ testId, status: 'ready', data: response.data });
    }).catch((error) => {
      if (current) setProgressState({ testId, status: 'error', error: getApiErrorMessage(error, 'не удалось загрузить прогресс назначений') });
    });
    return () => { current = false; };
  }, [cut, selectedTest, detailTab, progressRevision, requestProgress]);

  useEffect(() => {
    if (cut !== 'retake' || retakeCandidateTestIds.length === 0) {
      setRetakeProgressLoading(false);
      return undefined;
    }
    // Generation of this mode/candidate list/revision; old in-flight requests keep their slots until they settle.
    let current = true;
    const isCurrent = () => current;
    setRetakeProgressLoading(true);
    setRetakeProgressErrors({});
    Promise.all(retakeCandidateTestIds.map(async (testId) => {
      try {
        const response = await requestProgress(testId, isCurrent);
        if (!current || !response) return;
        const { data } = response;
        if (data?.test_id === testId && Array.isArray(data.rows)) {
          setRetakeProgress((previous) => ({ ...previous, [testId]: data }));
        } else {
          setRetakeProgressErrors((previous) => ({ ...previous, [testId]: 'Некорректные данные прогресса назначений' }));
        }
      } catch (error) {
        if (current) setRetakeProgressErrors((previous) => ({ ...previous, [testId]: getApiErrorMessage(error, 'не удалось загрузить прогресс назначений') }));
      }
    })).finally(() => { if (current) setRetakeProgressLoading(false); });
    return () => { current = false; };
  }, [cut, retakeCandidateTestIds, progressRevision, requestProgress]);

  useEffect(() => { setAttemptHistoryKey(null); }, [selectedTestId, detailTab, selectedUserId, cut]);

  const selectedUser = selectedUserId ? users.find((u) => u.id === selectedUserId) : null;
  const selectedUserAssignments = selectedUserId ? (byUser.get(selectedUserId) || []) : [];
  // Unique test ids of the selected employee; a string key keeps the effect from restarting on every reload().
  const userProgressKey = Array.from(new Set(selectedUserAssignments
    .map((item) => item.test_id)
    .filter((testId) => Number.isSafeInteger(testId) && testId > 0))).join(',');

  useEffect(() => {
    if (cut !== 'users' || !selectedUserId) {
      setUserProgress(null);
      return undefined;
    }
    const userId = selectedUserId;
    const testIds = userProgressKey ? userProgressKey.split(',').map(Number) : [];
    // Generation of this employee/mode/revision: cleanup drops late answers after unmount or a change of employee/mode.
    let current = true;
    const isCurrent = () => current;
    const initial: Record<number, UserProgressEntry> = {};
    testIds.forEach((testId) => { initial[testId] = { status: 'loading' }; });
    setUserProgress({ userId, tests: initial });
    const settle = (testId: number, entry: UserProgressEntry) => {
      if (!current) return;
      setUserProgress((previous) => (previous && previous.userId === userId
        ? { userId, tests: { ...previous.tests, [testId]: entry } } : previous));
    };
    // Each test queues on the page-wide limiter; one failure settles only its own entry.
    testIds.forEach(async (testId) => {
      try {
        const response = await requestProgress(testId, isCurrent);
        if (!response) return;
        const { data } = response;
        settle(testId, data?.test_id === testId && Array.isArray(data.rows)
          ? { status: 'ready', data }
          : { status: 'error', error: 'Некорректные данные прогресса назначений' });
      } catch (error) {
        settle(testId, { status: 'error', error: getApiErrorMessage(error, 'не удалось загрузить прогресс назначений') });
      }
    });
    return () => { current = false; };
  }, [cut, selectedUserId, userProgressKey, progressRevision, requestProgress]);

  const employeeColumns = [
    {
      title: 'Сотрудник', dataIndex: 'user_name', key: 'user',
      render: (_: unknown, row: Assignment) =>
        row.user_name || users.find((u) => u.id === row.user_id)?.full_name || '—',
    },
    {
      title: 'Состояние', key: 'state', width: 160,
      render: (_: unknown, row: Assignment) => <StateTag state={row.effective_state} />,
    },
    {
      title: 'Срок', key: 'due', width: 110,
      render: (_: unknown, row: Assignment) =>
        row.due_at ? dayjs(row.due_at).format('DD.MM.YYYY') : <Text type="secondary">—</Text>,
    },
    {
      title: '', key: 'actions', width: 200,
      render: (_: unknown, row: Assignment) => {
        const primary = row.effective_state === 'prepared'
          ? <Button size="small" type="primary" ghost disabled={busy}
              onClick={() => release(row)}>Выдать</Button>
          : row.effective_state === 'completed'
            ? <Button size="small" disabled={busy} onClick={() => retake(row)}>Пересдача</Button>
            : row.effective_state === 'available'
              ? <Button size="small" disabled={busy} onClick={() => withdraw(row)}>Приостановить</Button>
              : row.effective_state === 'paused'
                ? <Button size="small" type="primary" ghost disabled={busy}
                    onClick={() => release(row)}>Возобновить</Button>
                : row.effective_state === 'expired'
                  ? <Button size="small" disabled={busy} onClick={() => {
                      setRenewing(row);
                      setRenewDeadline(dayjs().add(7, 'day'));
                    }}>Продлить</Button>
                  : null;
        return (
          <Space size={4}>
            {primary}
            <Dropdown
              disabled={busy}
              menu={{
                items: [
                  { key: 'reassign', label: 'Назначить другому сотруднику' },
                  { type: 'divider' as const },
                  { key: 'delete', label: 'Удалить назначение', danger: true },
                ],
                onClick: ({ key }) => {
                  if (key === 'reassign') { setReassign(row); setReassignUserId(null); }
                  if (key === 'delete') deleteAssignment(row);
                },
              }}
            >
              <Button size="small">…</Button>
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  const employeeActions = employeeColumns[3];

  const renderAttemptHistoryTable = (historyRow?: AssignmentProgressRow) => (
    <Table<AssignmentProgressAttempt>
      size="small" pagination={false} scroll={{ x: 700, y: 400 }}
      rowKey={(attempt, index) => `${attempt.attempt_id}:${index}`}
      dataSource={historyRow ? progressAttempts(historyRow) : []}
      locale={{ emptyText: 'Попыток нет' }}
      columns={[
        { title: 'Попытка', key: 'number', width: 130,
          render: (_: unknown, __, index) => index === 0 ? 'Первая попытка' : `Пересдача №${index}` },
        { title: 'Дата · балл · состояние / результат', key: 'attempt',
          render: (_: unknown, attempt) => renderProgressAttempt(attempt) },
      ]}
    />
  );

  const renderAssignmentProgress = () => {
    if (!selectedTest || progressState?.testId !== selectedTest.id
      || progressState.status === 'loading') {
      return <Skeleton active paragraph={{ rows: 4 }} />;
    }
    if (progressState.status === 'error') {
      return <Alert type="error" showIcon message={progressState.error || 'Не удалось загрузить прогресс назначений'} />;
    }
    const data = progressState.data;
    if (!data || data.test_id !== selectedTest.id || !Array.isArray(data.rows)) {
      return <Alert type="error" showIcon message="Некорректные данные прогресса назначений" />;
    }
    const seen = new Set<string>();
    const rows = data.rows.filter((row) => {
      if (!row || typeof row !== 'object' || row.test_id !== selectedTest.id
        || ![row.assignment_id, row.user_id, row.test_id].every((id) => Number.isSafeInteger(id) && id > 0)) return false;
      const key = progressRowKey(row);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const historyRow = rows.find((row) => progressRowKey(row) === attemptHistoryKey);
    return (
      <>
        <Space size={6} wrap style={{ marginBottom: 10 }}>
          {PROGRESS_SUMMARY.map(({ key, label, color }) => {
            const value = data.summary?.[key];
            return <Tag key={key} color={color}>{label}: {typeof value === 'number'
              && Number.isSafeInteger(value) && value >= 0 ? value : '—'}</Tag>;
          })}
        </Space>
        <Table<AssignmentProgressRow>
          size="small" rowKey={progressRowKey} pagination={false}
          dataSource={rows} locale={{ emptyText: 'Тест ещё никому не назначен' }}
          scroll={{ x: 1034, y: 520 }}
          columns={[
            {
              title: 'Сотрудник', key: 'employee', width: 190,
              render: (_: unknown, row) => {
                const attempts = progressAttempts(row);
                return <Space direction="vertical" size={0}>
                  <Text>{typeof row.full_name === 'string' && row.full_name.trim()
                    ? row.full_name : `Сотрудник #${row.user_id}`}</Text>
                  {attempts.length > 3 && <Button type="link" size="small"
                    onClick={() => setAttemptHistoryKey(progressRowKey(row))}>
                    Ещё {attempts.length - 3}
                  </Button>}
                </Space>;
              },
            },
            ...['Первая попытка', 'Пересдача №1', 'Пересдача №2'].map((title, index) => ({
              title, key: `attempt-${index}`, width: 150,
              render: (_: unknown, row: AssignmentProgressRow) => renderProgressAttempt(progressAttempts(row)[index]),
            })),
            { title: 'Результат, %', key: 'result-percent', width: 84, render: (_: unknown, row) => renderProgressScore(row) },
            { title: 'Итог', key: 'final', width: 140, render: (_: unknown, row) => renderProgressFinal(row) },
            {
              title: 'Действие', key: 'actions', width: 170,
              render: (_: unknown, row) => {
                const assignment = selectedTestAssignments.find((item) => (
                  item.id === row.assignment_id && item.user_id === row.user_id && item.test_id === row.test_id
                ));
                if (!assignment) return <Text type="secondary">—</Text>;
                const needsRetake = row.flags?.needs_retake === true || row.status === 'needs_retake';
                return <Space size={4}>
                  {employeeActions.render(_, assignment)}
                  {needsRetake && assignment.effective_state !== 'completed'
                    && <Button size="small" disabled={busy} onClick={() => retake(assignment)}>Выдать пересдачу</Button>}
                </Space>;
              },
            },
          ]}
        />
        <Modal open={!!historyRow} title={`История попыток: ${typeof historyRow?.full_name === 'string'
          ? historyRow.full_name : `Сотрудник #${historyRow?.user_id}`}`}
          onCancel={() => setAttemptHistoryKey(null)} footer={null} width={900}>
          {renderAttemptHistoryTable(historyRow)}
        </Modal>
      </>
    );
  };

  const renderTestDetail = () => {
    if (!selectedTest) {
      return <Empty description="Выберите тест слева" style={{ padding: '60px 0' }} />;
    }
    const prepared = selectedTestAssignments.filter((a) => a.effective_state === 'prepared');
    return (
      <>
        <div className="ab-detail-head">
          <div>
            <Title level={4} style={{ marginBottom: 6 }}>{selectedTest.title}</Title>
            <Space size={6} wrap>
              {selectedTest.subgroup
                ? <Tag color="blue">{selectedTest.subgroup}</Tag>
                : <Tag>без категории</Tag>}
              {selectedTest.difficulty
                ? <Tag color={TEST_DIFFICULTY_COLOR[selectedTest.difficulty]}>
                    {TEST_DIFFICULTY_LABEL[selectedTest.difficulty]}
                  </Tag>
                : <Tag>сложность не указана</Tag>}
              <Tag>{selectedTest.time_limit_minutes} мин</Tag>
              <Tag>{selectedTest.questions_count ?? 0} вопросов</Tag>
              <Tag color={selectedTest.is_archived ? 'red' : (selectedTest.is_active ? 'green' : 'gold')}>
                {TEST_LIFECYCLE_LABEL[testLifecycle(selectedTest)]}
              </Tag>
            </Space>
          </div>
          <Space>
            {prepared.length > 0 && (
              <Button type="primary" loading={busy}
                onClick={() => releaseAllPrepared(selectedTestAssignments)}>
                Выдать подготовленные ({prepared.length})
              </Button>
            )}
            <Tooltip title={(selectedTest.questions_count ?? 0) === 0
              ? 'Сначала добавьте вопросы в тест' : undefined}>
            <Button icon={<UsergroupAddOutlined />}
              disabled={(selectedTest.questions_count ?? 0) === 0 || selectedTest.is_archived}
              onClick={() => setAssignOpen(true)}>
              Назначить
            </Button>
            </Tooltip>
            <Dropdown
              disabled={busy}
              menu={{
                items: [
                  { key: 'edit', icon: <EditOutlined />, label: 'Изменить параметры' },
                  { key: 'duplicate', icon: <CopyOutlined />, label: 'Дублировать' },
                  { key: 'template', label: 'Скачать шаблон Excel' },
                  { key: 'images', label: 'Импорт изображений из папки' },
                  {
                    key: 'catalog',
                    disabled: selectedTest.is_archived
                      || (!selectedTest.is_active && (selectedTest.questions_count ?? 0) === 0),
                    label: selectedTest.is_active
                      ? 'Убрать из общего каталога'
                      : 'Показать в общем каталоге',
                  },
                  {
                    key: 'archive',
                    label: selectedTest.is_archived ? 'Восстановить из архива' : 'Перенести в архив',
                  },
                  { type: 'divider' as const },
                  { key: 'delete', icon: <DeleteOutlined />, label: 'Удалить тест', danger: true },
                ],
                onClick: ({ key }) => {
                  if (key === 'edit') openEdit(selectedTest);
                  if (key === 'duplicate') duplicateTest(selectedTest);
                  if (key === 'template') downloadTemplate(selectedTest);
                  if (key === 'images') setImagesOpen(true);
                  if (key === 'catalog') {
                    patchTest(selectedTest, { is_active: !selectedTest.is_active },
                      selectedTest.is_active
                        ? 'Тест убран из общего каталога'
                        : 'Тест показан в общем каталоге');
                  }
                  if (key === 'archive') {
                    patchTest(
                      selectedTest,
                      { is_archived: !selectedTest.is_archived },
                      selectedTest.is_archived
                        ? 'Тест восстановлен как черновик'
                        : 'Тест перенесён в архив',
                    );
                  }
                  if (key === 'delete') removeTest(selectedTest);
                },
              }}
            >
              <Button>Ещё</Button>
            </Dropdown>
          </Space>
        </div>

        {prepared.length > 0 && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message={`Подготовлено ${prepared.length} — сотрудники этого пока не видят. Выдача откроет доступ.`} />
        )}

        {(selectedTest.questions_count ?? 0) === 0 && (
          <Alert type="error" showIcon style={{ marginBottom: 12 }}
            message="Тест нельзя назначить или опубликовать: в нём нет вопросов."
            action={<Button size="small" onClick={() => setDetailTab('questions')}>Добавить вопросы</Button>} />
        )}

        {selectedTest.is_archived && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message="Тест находится в архиве. Новые назначения и публикация недоступны." />
        )}

        <Tabs
          activeKey={detailTab} onChange={setDetailTab} size="small"
          items={[
            {
              key: 'people',
              label: `Кому назначен (${selectedTestAssignments.length})`,
              children: renderAssignmentProgress(),
            },
            {
              key: 'questions',
              label: `Вопросы (${selectedTest.questions_count ?? 0})`,
              children: (
                <div className="ab-question-table" ref={questionTableRef}>
                  {questionSearch && !questionsLoading && (
                    <Space className="ab-question-search-summary" wrap size={8}>
                      <Text>Найдено вопросов: {matchingQuestions.length}</Text>
                      {matchingQuestions.length > 0 && <>
                        <Text type="secondary">{activeQuestionMatchIndex + 1} из {matchingQuestions.length}</Text>
                        <Button size="small" aria-label="Предыдущий найденный вопрос"
                          disabled={activeQuestionMatchIndex === 0}
                          onClick={() => setActiveQuestionMatchId(matchingQuestions[activeQuestionMatchIndex - 1].id)}>
                          Предыдущий
                        </Button>
                        <Button size="small" aria-label="Следующий найденный вопрос"
                          disabled={activeQuestionMatchIndex === matchingQuestions.length - 1}
                          onClick={() => setActiveQuestionMatchId(matchingQuestions[activeQuestionMatchIndex + 1].id)}>
                          Следующий
                        </Button>
                      </>}
                      <Button size="small" onClick={() => setQuery('')}>Сбросить поиск</Button>
                    </Space>
                  )}
                <Table<EditableQuestion>
                  size="small" rowKey="id" loading={questionsLoading}
                  dataSource={displayedQuestions} pagination={false} scroll={{ y: 340 }}
                  rowClassName={(row) => questionSearch && matchesSearch(row.text, questionSearch)
                    ? `ab-question-match${row.id === activeQuestionMatchId ? ' ab-question-match--active' : ''}` : ''}
                  locale={{ emptyText: 'В тесте нет вопросов' }}
                  columns={[
                    {
                      title: '№', dataIndex: 'order', key: 'order', width: 50,
                      render: (_: unknown, __: EditableQuestion, index: number) => index + 1,
                    },
                    {
                      title: 'Вопрос', dataIndex: 'text', key: 'text',
                      render: (value: string, row) => (
                        <div>
                          <div className="ab-question-text">{highlightQuestionMatch(value.trim(), questionSearch)}</div>
                          <Space size={4} wrap style={{ marginTop: 4 }}>
                            {row.answers.filter((a) => a.is_correct).map((a) => (
                              <Tag key={a.id} color="green">{a.text.trim().slice(0, 40)}</Tag>
                            ))}
                            {row.image_url && <Tag>с изображением</Tag>}
                          </Space>
                        </div>
                      ),
                    },
                    {
                      title: '', key: 'edit', width: 100,
                      render: (_: unknown, row) => (
                        <Button size="small" onClick={() => setEditing(row)}>Править</Button>
                      ),
                    },
                  ]}
                />
                </div>
              ),
            },
          ]}
        />
      </>
    );
  };

  const renderUserDetail = () => {
    if (!selectedUser) {
      return <Empty description="Выберите сотрудника слева" style={{ padding: '60px 0' }} />;
    }
    const progressTests = userProgress && userProgress.userId === selectedUser.id ? userProgress.tests : {};
    // Exact join only: assignment_id + user_id + test_id; anything else is treated as no progress.
    const exactProgressRow = (item: Assignment): AssignmentProgressRow | undefined => {
      const entry = progressTests[item.test_id];
      if (entry?.status !== 'ready' || !entry.data || entry.data.test_id !== item.test_id
        || !Array.isArray(entry.data.rows)) return undefined;
      return entry.data.rows.find((candidate) => !!candidate && typeof candidate === 'object'
        && candidate.assignment_id === item.id && candidate.user_id === item.user_id
        && candidate.test_id === item.test_id);
    };
    const testLabel = (testId: number, withPrefix = true) => {
      const title = tests.find((test) => test.id === testId)?.title
        || selectedUserAssignments.find((item) => item.test_id === testId)?.test_title;
      const label = title ? `«${title}» (#${testId})` : `#${testId}`;
      return withPrefix ? `Тест ${label}` : label;
    };
    const missingProgress = (item: Assignment) => {
      const entry = progressTests[item.test_id];
      const pending = entry ? entry.status === 'loading' : Number.isSafeInteger(item.test_id) && item.test_id > 0;
      return pending ? <Text type="secondary">Загрузка…</Text> : <Tag>Нет данных</Tag>;
    };
    const progressErrors = Object.keys(progressTests).map(Number)
      .filter((testId) => progressTests[testId].status === 'error'
        && selectedUserAssignments.some((item) => item.test_id === testId))
      .map((testId) => [testId, progressTests[testId].error || 'не удалось загрузить прогресс назначений'] as const);
    const historyRow = selectedUserAssignments.map(exactProgressRow)
      .find((progress) => !!progress && progressRowKey(progress) === attemptHistoryKey);
    const prepared = selectedUserAssignments.filter((a) => a.effective_state === 'prepared');
    return (
      <>
        <div className="ab-detail-head">
          <div>
            <Title level={4} style={{ marginBottom: 6 }}>{selectedUser.full_name}</Title>
            <Space size={6} wrap>
              <Tag>{selectedUser.department || 'без отдела'}</Tag>
              {selectedUser.role === 'admin' && <Tag color="purple">админ</Tag>}
            </Space>
          </div>
          {prepared.length > 0 && (
            <Button type="primary" loading={busy}
              onClick={() => releaseAllPrepared(selectedUserAssignments)}>
              Выдать подготовленные ({prepared.length})
            </Button>
          )}
        </div>
        <Space size={6} wrap style={{ marginBottom: 12 }}>
          {stateSummary(selectedUserAssignments)}
          {!selectedUserAssignments.length && <Text type="secondary">Назначений нет</Text>}
        </Space>
        {mergeIds.length >= 2 && (
          <Alert
            type="info" showIcon style={{ marginBottom: 10 }}
            message={`Выбрано назначений: ${mergeIds.length}`}
            action={(
              <Button size="small" type="primary" onClick={() => {
                setMergeTitle(`Объединённый тест (${selectedUser.full_name})`);
                setMergeOpen(true);
              }}>
                Объединить в новый тест
              </Button>
            )}
          />
        )}
        {progressErrors.map(([testId, error]) => (
          <Alert key={testId} type="error" showIcon style={{ marginBottom: 8 }}
            message={`${testLabel(testId)}: ${error}`} />
        ))}
        <Title level={5}>Текущие назначения ({selectedUserAssignments.length})</Title>
        <Table<Assignment>
          size="small" rowKey="id" pagination={false} scroll={{ x: 1080, y: 520 }}
          dataSource={selectedUserAssignments}
          locale={{ emptyText: 'Назначений нет' }}
          rowSelection={{
            selectedRowKeys: mergeIds,
            onChange: (keys) => setMergeIds(keys as number[]),
            getCheckboxProps: (row) => ({
              disabled: row.effective_state === 'completed',
            }),
          }}
          columns={[
            {
              title: 'Тест', key: 'test', width: 210,
              render: (_: unknown, item: Assignment) => {
                const progress = exactProgressRow(item);
                const attempts = progress ? progressAttempts(progress) : [];
                return <Space direction="vertical" size={0}>
                  <Text>{item.test_title || tests.find((test) => test.id === item.test_id)?.title
                    || `Тест #${item.test_id}`}</Text>
                  <Text type="secondary">#{item.test_id}</Text>
                  {attempts.length > 3 && <Button type="link" size="small"
                    onClick={() => setAttemptHistoryKey(`${item.id}:${item.user_id}:${item.test_id}`)}>
                    Ещё {attempts.length - 3}
                  </Button>}
                </Space>;
              },
            },
            ...['Первая попытка', 'Пересдача №1', 'Пересдача №2'].map((title, index) => ({
              title, key: `attempt-${index}`, width: 150,
              render: (_: unknown, item: Assignment) => {
                const progress = exactProgressRow(item);
                return progress ? renderProgressAttempt(progressAttempts(progress)[index]) : missingProgress(item);
              },
            })),
            {
              title: 'Результат, %', key: 'result-percent', width: 84,
              render: (_: unknown, item: Assignment) => {
                const progress = exactProgressRow(item);
                return progress ? renderProgressScore(progress) : missingProgress(item);
              },
            },
            {
              title: 'Итог', key: 'final', width: 140,
              render: (_: unknown, item: Assignment) => {
                const progress = exactProgressRow(item);
                return progress ? renderProgressFinal(progress) : missingProgress(item);
              },
            },
            {
              title: 'Действие', key: 'actions', width: 164,
              render: (_: unknown, item: Assignment) => {
                const progress = exactProgressRow(item);
                const needsRetake = !!progress
                  && (progress.flags?.needs_retake === true || progress.status === 'needs_retake');
                return <Space size={4}>
                  {employeeActions.render(_, item)}
                  {needsRetake && item.effective_state !== 'completed'
                    && <Button size="small" disabled={busy} onClick={() => retake(item)}>Выдать пересдачу</Button>}
                </Space>;
              },
            },
          ]}
        />
        <EmployeeCompletionHistory userId={selectedUser.id} revision={progressRevision} />
        <Modal open={!!historyRow} title={`История попыток: ${historyRow
          ? testLabel(historyRow.test_id, false) : ''}`}
          onCancel={() => setAttemptHistoryKey(null)} footer={null} width={900}>
          {renderAttemptHistoryTable(historyRow)}
        </Modal>
      </>
    );
  };

  const renderRetake = () => (
    <>
      <Title level={4}>Нужна пересдача</Title>
      <Paragraph type="secondary">
        Показаны назначения с итоговым статусом прогресса «Нужна пересдача». История попыток сохраняется.
      </Paragraph>
      {Object.entries(retakeProgressErrors).map(([testId, error]) => (
        <Alert key={testId} type="error" showIcon message={`Тест #${testId}: ${error}`} style={{ marginBottom: 8 }} />
      ))}
      {retakeProgressLoading && retakeCandidates.length === 0 && <Skeleton active paragraph={{ rows: 3 }} />}
      {!retakeProgressLoading && retakeCandidateTestIds.length === 0 && (
        <Empty description="Нет завершённых назначений с возможной неудачной попыткой" />
      )}
      {!retakeProgressLoading && retakeCandidateTestIds.length > 0 && retakeCandidates.length === 0
        && Object.keys(retakeProgressErrors).length === 0 && (
          <Empty description="Нет назначений, которым действительно нужна пересдача" />
        )}
      <Table<{ row: AssignmentProgressRow; assignment: Assignment }>
        size="small" rowKey={({ row }) => progressRowKey(row)} dataSource={retakeCandidates}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        locale={{ emptyText: retakeProgressLoading ? 'Загрузка прогресса…' : 'Нет пересдач' }}
        columns={[
          { title: 'Сотрудник', key: 'user', render: (_: unknown, item) => item.row.full_name || item.assignment.user_name || `Сотрудник #${item.row.user_id}` },
          { title: 'Тест', key: 'test', render: (_: unknown, item) => tests.find((test) => test.id === item.row.test_id)?.title || item.assignment.test_title || `Тест #${item.row.test_id}` },
          {
            title: 'Последний результат', key: 'score', width: 170,
            render: (_: unknown, item) => renderProgressFinal(item.row),
          },
          {
            title: '', key: 'a', width: 140,
            render: (_: unknown, item) => (
              <Button size="small" disabled={busy} onClick={() => retake(item.assignment)}>
                Выдать пересдачу
              </Button>
            ),
          },
        ]}
      />
    </>
  );

  return (
    <Layout wide>
      <div data-guide="assignments-home" className="ab-page">
        <div className="ab-head">
          <div>
            <Title level={3} style={{ marginBottom: 2 }}>Назначения</Title>
            <Text type="secondary">
              Одно состояние вместо пары флагов: подготовленное отделено от приостановленного.
            </Text>
          </div>
          <Space>
            <Button onClick={openHistory}>История назначений</Button>
            <Button onClick={() => navigate('/admin/assignments')}>
              Массовое назначение и создание теста
            </Button>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Обновить</Button>
          </Space>
        </div>

        <Card size="small" className="ab-filters">
          <Space wrap size={10} style={{ width: '100%' }}>
            <Segmented
              value={cut}
              onChange={(value) => { setCut(value as Cut); }}
              options={[
                { label: 'По тестам', value: 'tests' },
                { label: 'По сотрудникам', value: 'users' },
                { label: 'Пересдачи', value: 'retake' },
              ]}
            />
            <Input
              className="ab-filter-search"
              allowClear prefix={<SearchOutlined />} style={{ width: 230 }}
              aria-label={cut === 'users' ? 'Поиск сотрудников' : 'Поиск тестов и вопросов'}
              placeholder={cut === 'users' ? 'Имя или отдел…' : 'Тест, категория или вопрос…'}
              value={query} onChange={(e) => setQuery(e.target.value)}
            />
            {cut === 'tests' && (
              <>
                <Select
                  allowClear showSearch style={{ width: 230 }} placeholder="Категория"
                  value={subgroup} onChange={setSubgroup}
                  options={subgroups.map((s) => ({ value: s, label: s }))}
                />
                <Select
                  allowClear style={{ width: 170 }} placeholder="Сложность"
                  value={difficulty} onChange={setDifficulty}
                  options={[
                    ...TEST_DIFFICULTY_OPTIONS,
                    { value: 'unset', label: 'Не указана' },
                  ]}
                />
                <Select
                  allowClear style={{ width: 160 }} placeholder="Период"
                  value={month} onChange={setMonth}
                  options={months.map((m) => ({ value: m, label: monthLabel(m) }))}
                />
                <Select
                  allowClear style={{ width: 150 }} placeholder="Жизненный цикл"
                  value={lifecycle} onChange={setLifecycle}
                  options={(Object.keys(TEST_LIFECYCLE_LABEL) as TestLifecycle[]).map((value) => ({
                    value, label: TEST_LIFECYCLE_LABEL[value],
                  }))}
                />
              </>
            )}
            <Text type="secondary" style={{ marginLeft: 'auto' }}>
              {cut === 'users'
                ? `${visibleUsers.length} сотрудников`
                : `Показано ${visibleTests.length} из ${tests.length}`}
            </Text>
          </Space>

          <div className="ab-chips">
            <Text type="secondary" style={{ fontSize: 12 }}>Состояние назначений</Text>
            {STATE_ORDER.filter((s) => stateTotals[s]).map((s) => (
              <Tooltip key={s} title={
                s === 'prepared' ? 'Создано заранее, сотрудник пока не видит'
                  : s === 'paused' ? 'Доступ был и его закрыли'
                    : undefined}>
                <button
                  type="button"
                  className={`ab-chip ab-chip--${s}${states.includes(s) ? ' is-on' : ''}`}
                  onClick={() => setStates((prev) => prev.includes(s)
                    ? prev.filter((x) => x !== s)
                    : [...prev, s])}
                >
                  {STATE_LABEL[s]} <i>{stateTotals[s]}</i>
                </button>
              </Tooltip>
            ))}
            {(states.length || subgroup || difficulty || month || lifecycle || query) ? (
              <Button size="small" type="link" onClick={() => {
                setStates([]); setSubgroup(undefined); setMonth(undefined);
                setDifficulty(undefined); setLifecycle(undefined); setQuery('');
              }}>Сбросить</Button>
            ) : null}
          </div>
        </Card>

        {loading ? (
          <Card><Skeleton active paragraph={{ rows: 8 }} /></Card>
        ) : cut === 'retake' ? (
          <Card>{renderRetake()}</Card>
        ) : (
          <div className="ab-split">
            <Card size="small" className="ab-list-card"
              title={cut === 'tests' ? 'Тесты' : 'Сотрудники'}>
              <div className="ab-list">
                {cut === 'tests' ? visibleTests.map((test) => {
                  const own = byTest.get(test.id) || [];
                  return (
                    <button type="button" key={test.id}
                      className={`ab-row${selectedTestId === test.id ? ' is-on' : ''}`}
                      onClick={() => selectTest(test)}>
                      <span className="ab-row__main">
                        <span className="ab-row__title">{test.title}</span>
                        <span className="ab-row__meta">
                          {own.length ? stateSummary(own)
                            : <Tag style={{ margin: 0 }}>нет назначений</Tag>}
                          {questionSearch && testQuestionTexts(test).some((text) => matchesSearch(text, questionSearch)) && (
                            <Tag color="gold" style={{ margin: 0 }}>Вопросов найдено: {testQuestionTexts(test).filter((text) => matchesSearch(text, questionSearch)).length}</Tag>
                          )}
                          {test.subgroup && <Tag color="blue" style={{ margin: 0 }}>{test.subgroup}</Tag>}
                          {test.difficulty
                            ? <Tag color={TEST_DIFFICULTY_COLOR[test.difficulty]} style={{ margin: 0 }}>
                                {TEST_DIFFICULTY_LABEL[test.difficulty]}
                              </Tag>
                            : <Tag style={{ margin: 0 }}>сложность не указана</Tag>}
                          <Tag color={test.is_archived ? 'red' : (test.is_active ? 'green' : 'gold')}
                            style={{ margin: 0 }}>
                            {TEST_LIFECYCLE_LABEL[testLifecycle(test)]}
                          </Tag>
                        </span>
                      </span>
                      <span className="ab-row__nums">
                        <b>{test.questions_count ?? 0}</b><s>вопр.</s>
                      </span>
                    </button>
                  );
                }) : visibleUsers.map((item) => {
                  const own = byUser.get(item.id) || [];
                  return (
                    <button type="button" key={item.id}
                      className={`ab-row${selectedUserId === item.id ? ' is-on' : ''}`}
                      onClick={() => setSelectedUserId(item.id)}>
                      <span className="ab-row__main">
                        <span className="ab-row__title">{item.full_name}</span>
                        <span className="ab-row__meta">
                          {own.length ? stateSummary(own)
                            : <Tag style={{ margin: 0 }}>нет назначений</Tag>}
                        </span>
                      </span>
                    </button>
                  );
                })}
                {!((cut === 'tests' ? visibleTests : visibleUsers).length) && (
                  <Empty description="Ничего не найдено" style={{ padding: '40px 0' }} />
                )}
              </div>
            </Card>
            <Card size="small" className="ab-detail-card">
              {cut === 'tests' ? renderTestDetail() : renderUserDetail()}
            </Card>
          </div>
        )}
      </div>

      <Modal
        open={!!editTest}
        title="Параметры теста"
        onCancel={() => setEditTest(null)}
        okText="Сохранить"
        okButtonProps={{ loading: busy }}
        onOk={async () => {
          if (!editTest) return;
          await patchTest(editTest, {
            title: editDraft.title,
            description: editDraft.description || null,
            subgroup: editDraft.subgroup || null,
            difficulty: editDraft.difficulty || null,
            time_limit_minutes: editDraft.time_limit_minutes,
          }, 'Параметры сохранены');
          setEditTest(null);
        }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Text type="secondary">Название</Text>
            <Input
              value={editDraft.title}
              onChange={(e) => setEditDraft((d) => ({ ...d, title: e.target.value }))}
            />
          </div>
          <div>
            <Text type="secondary">Описание</Text>
            <Input.TextArea
              value={editDraft.description} autoSize={{ minRows: 2, maxRows: 4 }}
              onChange={(e) => setEditDraft((d) => ({ ...d, description: e.target.value }))}
            />
          </div>
          <div>
            <Text type="secondary">Категория</Text>
            <Select
              allowClear showSearch style={{ width: '100%' }}
              value={editDraft.subgroup || undefined}
              onChange={(value) => setEditDraft((d) => ({ ...d, subgroup: value || '' }))}
              options={subgroups.map((s) => ({ value: s, label: s }))}
            />
          </div>
          <div>
            <Text type="secondary">Уровень сложности</Text>
            <Select
              allowClear style={{ width: '100%' }} placeholder="Не указан"
              value={editDraft.difficulty || undefined}
              onChange={(value) => setEditDraft((d) => ({
                ...d, difficulty: (value || '') as TestDifficulty | '',
              }))}
              options={TEST_DIFFICULTY_OPTIONS}
            />
          </div>
          <div>
            <Text type="secondary">Ограничение по времени, мин</Text>
            <Input
              type="number" min={1} max={300}
              value={editDraft.time_limit_minutes}
              onChange={(e) => setEditDraft((d) => ({
                ...d, time_limit_minutes: Number(e.target.value) || 1,
              }))}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        open={!!reassign}
        title="Назначить другому сотруднику"
        onCancel={() => setReassign(null)}
        onOk={submitReassign}
        okText="Назначить"
        okButtonProps={{ loading: busy, disabled: !reassignUserId }}
      >
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Text type="secondary">
            Будет создана копия теста и назначена выбранному сотруднику. Исходный
            тест и назначение у {reassign?.user_name} останутся на месте.
          </Text>
          <Select
            showSearch style={{ width: '100%' }} placeholder="Выберите сотрудника"
            value={reassignUserId} onChange={setReassignUserId}
            optionFilterProp="label"
            options={users
              .filter((u) => u.role === 'employee' && u.id !== reassign?.user_id)
              .map((u) => ({ value: u.id, label: `${u.full_name} · ${u.department || 'без отдела'}` }))}
          />
        </Space>
      </Modal>

      <Modal
        open={imagesOpen}
        title="Импорт изображений из папки"
        onCancel={() => setImagesOpen(false)}
        onOk={importImages}
        okText="Импортировать"
        okButtonProps={{ loading: busy, disabled: !imagesPath.trim() }}
      >
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Text type="secondary">
            Путь к папке на сервере. Каждая вложенная папка станет категорией,
            а изображения внутри — вопросами.
          </Text>
          <Input
            value={imagesPath} onChange={(e) => setImagesPath(e.target.value)}
            placeholder="/data/source_images/brands"
          />
        </Space>
      </Modal>

      <Modal
        open={mergeOpen}
        title="Объединить назначения в один тест"
        onCancel={() => setMergeOpen(false)}
        onOk={submitMerge}
        okText="Объединить"
        okButtonProps={{ loading: busy, disabled: !mergeTitle.trim() }}
        width={580}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary">
            Вопросы выбранных тестов соберутся в один новый тест и будут назначены
            сотруднику. Полные повторы схлопнутся в один вопрос; вопросы с
            одинаковой формулировкой, но разными вариантами, сохранятся оба.
          </Text>
          <div>
            <Text type="secondary">Название нового теста</Text>
            <Input value={mergeTitle} onChange={(e) => setMergeTitle(e.target.value)} />
          </div>
          <div>
            <Text type="secondary">Ограничение по времени, мин</Text>
            <Input
              type="number" min={1} max={300} value={mergeLimit}
              onChange={(e) => setMergeLimit(Number(e.target.value) || 1)}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        open={historyOpen}
        title="История назначений по сотрудникам"
        onCancel={() => setHistoryOpen(false)}
        footer={null}
        width={900}
      >
        {historyLoading ? <Skeleton active paragraph={{ rows: 6 }} /> : (
          <Table
            size="small" rowKey="key"
            dataSource={history} pagination={{ pageSize: 12 }}
            locale={{ emptyText: 'История пуста' }}
            columns={[
              { title: 'Сотрудник', dataIndex: 'user_name', key: 'user' },
              { title: 'Месяц', dataIndex: 'month_label', key: 'month', width: 140 },
              {
                title: 'Тесты', key: 'tests',
                render: (_: unknown, row: any) => (
                  <Space size={4} wrap>
                    {(row.tests || []).map((t: any) => {
                      const eventLabel = HISTORY_EVENT_LABEL[t.event_type] || t.event_type;
                      const when = t.event_at
                        ? dayjs(t.event_at).format('DD.MM.YYYY HH:mm') : 'без даты';
                      return (
                        <Tooltip key={t.history_id}
                          title={`${when} · ${eventLabel}${t.assigned_by_name
                            ? ` · ${t.assigned_by_name}` : ''}`}>
                          <Tag>{t.test_title || t.title} · {eventLabel}</Tag>
                        </Tooltip>
                      );
                    })}
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Modal>

      <QuestionInlineEditor
        question={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { if (selectedTestId) void refreshAfterQuestionEdit(selectedTestId); }}
      />

      <Modal
        open={assignOpen}
        title={`Назначить: ${selectedTest?.title || ''}`}
        onCancel={() => setAssignOpen(false)}
        onOk={submitAssign}
        okText={releaseNow ? 'Выдать' : 'Подготовить'}
        okButtonProps={{ disabled: !assignUserIds.length, loading: busy }}
        width={620}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Text type="secondary">Сотрудники</Text>
            <Select
              mode="multiple" allowClear style={{ width: '100%' }}
              placeholder="Выберите сотрудников"
              value={assignUserIds} onChange={setAssignUserIds}
              optionFilterProp="label"
              options={users.filter((u) => u.role === 'employee').map((u) => ({
                value: u.id, label: `${u.full_name} · ${u.department || 'без отдела'}`,
              }))}
            />
          </div>
          <div>
            <Text type="secondary">Срок выполнения</Text>
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY"
              value={deadline} onChange={setDeadline} allowClear />
          </div>
          <Checkbox checked={releaseNow} onChange={(e) => setReleaseNow(e.target.checked)}>
            Сразу открыть сотрудникам
          </Checkbox>
          <Alert
            type={releaseNow ? 'success' : 'info'} showIcon
            message={releaseNow
              ? 'Назначение будет сразу видно сотрудникам'
              : 'Назначение сохранится подготовленным — сотрудники его не увидят, пока вы не выдадите доступ'}
          />
        </Space>
      </Modal>

      <Modal
        open={!!renewing}
        title={`Продлить назначение: ${renewing?.test_title || ''}`}
        onCancel={() => { setRenewing(null); setRenewDeadline(null); }}
        onOk={renewExpired}
        okText="Продлить и открыть"
        okButtonProps={{ loading: busy, disabled: !renewDeadline }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary">
            Сотрудник: {renewing?.user_name}. Назначение снова станет доступным,
            прежние попытки и результаты сохранятся.
          </Text>
          <div>
            <Text type="secondary">Новый срок выполнения</Text>
            <DatePicker
              style={{ width: '100%' }} format="DD.MM.YYYY"
              value={renewDeadline} onChange={setRenewDeadline}
              disabledDate={(current) => current.endOf('day').isBefore(dayjs())}
            />
          </div>
        </Space>
      </Modal>
    </Layout>
  );
};

export default AdminAssignmentBoard;
